import z from 'zod';
import compact from 'just-compact';
import {
  Languages,
  PageTypePrefixes,
  defaultLanguage
} from '@stereoagency/constants';
import groq from 'groq';
import $globalSanityClient from '@/sanity/$globalSanityClient';

const allPagesAndTranslationsQuery = [
  groq`*[_type == "page" && language == "en" && defined(slug.current) && defined(language)] {
    _id,
    slug,
    language,
    "translations": *[_type == "translation.metadata" && ^._id in translations[].value._ref][0].translations[].value->{
      _id,
      slug,
      language
    }
  }`,
  z.array(
    z
      .object({
        _id: z.string(),
        language: z.string(),
        slug: z.object({ current: z.string() }),
        translations: z
          .array(
            z
              .object({
                _id: z.string(),
                language: z.string(),
                slug: z.object({ current: z.string() })
              })
              .partial()
              .or(z.null())
          )
          .nullable()
      })
      .partial()
  )
] as const;

const allOtherDocumentsQuery = [
  `[
    ...*[_type=="article"],
    ...*[_type=="event" && datetime > now()],
    ...*[_type in ["researchGroup", "researchPlatform", "researchProject"] && defined(slug.current)]
  ] { _type, slug }`,
  z.array(
    z.object({
      _type: z.enum([
        'researchGroup',
        'researchProject',
        'researchPlatform',
        'article',
        'event'
      ]),
      slug: z.object({ current: z.string() })
    })
  )
] as const;

export interface Params {
  lang: string;
  slug: string[];
}

export default async function fetchStaticParams(
  languages = Languages
): Promise<Params[]> {
  // for the pages - we'll create params for all slugs in all languages
  // this means there may be dutch slugs in the english language
  // these will then be redirected to the translated slug if available
  // and should have the translated cannonical url
  const [allPageTranslations, allOtherDocuments] = await Promise.all([
    $globalSanityClient.fetch<
      z.infer<(typeof allPagesAndTranslationsQuery)[1]>
    >(allPagesAndTranslationsQuery[0]),
    $globalSanityClient.fetch<z.infer<(typeof allOtherDocumentsQuery)[1]>>(
      allOtherDocumentsQuery[0]
    )
  ]);

  /** create page params */
  const allPageTranslationsResult =
    allPagesAndTranslationsQuery[1].safeParse(allPageTranslations);
  const allPageParams = allPageTranslationsResult.success
    ? allPageTranslationsResult.data.reduce<Params[]>((acc, entry) => {
        if (!entry.language || !entry.slug?.current) {
          return acc;
        }

        acc.push({
          lang: entry.language,
          slug: compact(entry.slug.current.split('/'))
        });
        for (const lang of languages) {
          if (lang.id === defaultLanguage.id) continue;
          const translation =
            compact(entry.translations ?? []).find(
              (t) => t.language === lang.id
            ) ?? entry;
          if (translation.slug?.current) {
            acc.push({
              lang: lang.id,
              slug: translation.slug.current.split('/')
            });
          }
        }

        return acc;
      }, [])
    : [];

  /* create all other pages */
  const allOtherDocumentsResult =
    allOtherDocumentsQuery[1].safeParse(allOtherDocuments);
  const allOtherParams = allOtherDocumentsResult.success
    ? allOtherDocumentsResult.data.reduce<Params[]>(
        (acc, doc) =>
          acc.concat(
            languages.map((lang) => {
              const prefixes = PageTypePrefixes[lang.id];

              return {
                lang: lang.id,
                slug: [
                  prefixes[doc._type],
                  ...compact(doc.slug.current.split('/'))
                ]
              };
            })
          ),
        []
      )
    : [];

  return [
    /* include homepages */
    ...Languages.map((lang) => ({
      lang: lang.id,
      slug: []
    })),
    ...allPageParams,
    ...allOtherParams
  ];
}
